import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import TextareaInput from '../../form/base/TextareaInput';
import {
  AcknowledgmentValues,
  GetWaiversData,
  acknowledgmentSettingsSchema,
} from '../../../types/waivers';
import { important } from '../../../utils/global/helper';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';

import '../../form/form.css';
import OptionsList from '../OptionsList';

interface Props {
  onSuccess?: () => void;
  draft: GetWaiversData | null | undefined;
}

export default function AcknowledgmentsForm({ onSuccess, draft }: Props) {
  const [formError, setFormError] = React.useState('');

  const { mutation, isPending } = useUpdateWaiver({ draft });

  const [initialFormState] = React.useState<AcknowledgmentValues>({
    intro: draft?.acknowledgmentSettings?.intro || '',
    options: (draft?.acknowledgmentSettings?.options || []).sort(
      (a, b) => a.order - b.order,
    ),
  });

  const onSubmit = async (values: AcknowledgmentValues) => {
    if (!draft?.businessId || isPending) return;
    try {
      setFormError('');

      const response = await mutation.mutateAsync({
        acknowledgmentSettings: {
          intro: values.intro,
          options: values.options,
        },
      });

      if (response) {
        toast.success('Acknowledgment settings saved successfully');
        onSuccess?.();
      }
    } catch {
      toast.error('Failed to save acknowledgment settings');
    }
  };

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={acknowledgmentSettingsSchema}
        onSubmit={onSubmit}>
        {({ isSubmitting, isValid, dirty, values, setFieldValue }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}
            <Box width="100%">
              <TextareaInput
                name="intro"
                disabled={isSubmitting}
                autoHeight
                onlyHoverBorder
                sx={theme => ({
                  fontSize: important(theme.typography.h3.fontSize),
                })}
              />

              <OptionsList
                options={values.options}
                onChange={newOptions => setFieldValue('options', newOptions)}
                placeholder="Type question"
                addText="Add new question"
                askForType
                combineAllOptions
                disabled={isSubmitting}
                allowedTypes={['CHECKBOX', 'MULTILINE_TEXT']}
                warnOn18
              />
            </Box>

            <LoadingButton
              type="submit"
              disabled={!isValid || isSubmitting || !dirty || isPending}
              variant="contained"
              className="primary"
              loading={isSubmitting}>
              <span>Save</span>
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
